import React, { useMemo, useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import useCompanies from "../hooks/useCompanies";
import getForexPrices from "../utils/getForexPrices";
import { getNextZ } from "../utils/zIndexManager";
import "./modalStyles.css";

export default function CurrencyExchangeModal({ onClose }) {
  const companies = useCompanies();
  const prices = useMemo(() => getForexPrices(companies), [companies]);
  const enrichedPrices = useMemo(() => ({ USD: 1, ...(prices || {}) }), [prices]);
  
  // Get unique tickers for the headers
  const tickers = useMemo(() => Object.keys(enrichedPrices).sort(), [enrichedPrices]);

  // Draggable + zIndex Logic (unmodified)
  const modalRef = useRef(null);
  const [drag, setDrag] = useState({ isDragging: false, offsetX: 0, offsetY: 0 });
  const [zIndex, setZIndex] = useState(() => getNextZ() + 10000);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;
    const { innerWidth, innerHeight } = window;
    const { offsetWidth, offsetHeight } = modal;
    setPosition({
      top: innerHeight / 2 - offsetHeight / 2,
      left: innerWidth / 2 - offsetWidth / 2,
    });
  }, []);

  const handleMouseDown = (e) => {
    setZIndex(getNextZ() + 10000);
    const rect = modalRef.current.getBoundingClientRect();
    setDrag({ isDragging: true, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top });
  };

  const handleMouseMove = (e) => {
    if (!drag.isDragging) return;
    setPosition({ top: e.clientY - drag.offsetY, left: e.clientX - drag.offsetX });
  };

  const handleMouseUp = () => setDrag(prev => ({ ...prev, isDragging: false }));

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  });

  return ReactDOM.createPortal(
    <div
      ref={modalRef}
      className="floating-modal-container"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: "none",
        zIndex,
        width: "auto", // Allow it to expand to table size
        minWidth: "500px"
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="draggable-title">
        <span>CONVERSION RATES</span>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>
      
      <div className="scrollable-content" style={{ padding: "10px" }}>
        <table className="currency-exchange-table" style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #333", color: "#888", fontSize: "0.6rem", padding: "8px" }}>FROM \ TO</th>
              {tickers.map(t => (
                <th key={t} style={{ border: "1px solid #333", color: "#50E3C2", padding: "8px", fontSize: "0.7rem" }}>{t}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tickers.map(fromTicker => (
              <tr key={fromTicker}>
                <td style={{ border: "1px solid #333", color: "#50E3C2", fontWeight: "bold", padding: "8px", fontSize: "0.7rem", textAlign: "center" }}>
                  {fromTicker}
                </td>
                {tickers.map(toTicker => {
                  if (fromTicker === toTicker) {
                    return <td key={toTicker} style={{ border: "1px solid #333", color: "#444", textAlign: "center", fontSize: "0.7rem" }}>--</td>;
                  }
                  const rate = enrichedPrices[fromTicker] / enrichedPrices[toTicker];
                  return (
                    <td 
                      key={toTicker} 
                      style={{ 
                        border: "1px solid #333", 
                        padding: "8px", 
                        textAlign: "center",
                        fontSize: "0.75rem",
                        color: rate > 1 ? "#7ED957" : rate < 1 ? "#FF5E5E" : "#fff"
                      }}
                    >
                      {rate.toFixed(4)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>,
    document.body
  );
}